"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";

/**
 * Collapsed "Show investigation" wrapper for finished turns.
 * Callers: InsightsChat. forceOpen opens when Tasks rail targets a nested task.
 * User: scroll to task / investigation and open it.
 */
export function InsightsInvestigationCollapse({
  toolCount,
  noteCount,
  defaultOpen = false,
  forceOpen = false,
  children,
}: {
  toolCount: number;
  noteCount: number;
  defaultOpen?: boolean;
  /** Open from outside (e.g. Tasks rail click targeting a nested task). */
  forceOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen || forceOpen);
  const [prevForced, setPrevForced] = useState(forceOpen);
  if (forceOpen !== prevForced) {
    setPrevForced(forceOpen);
    if (forceOpen) setOpen(true);
  }

  if (toolCount === 0 && noteCount === 0) return <>{children}</>;

  const label = `Show investigation (${toolCount} tool${toolCount === 1 ? "" : "s"} · ${noteCount} note${noteCount === 1 ? "" : "s"})`;

  return (
    <div className="w-full min-w-0 max-w-[95%] space-y-2">
      <button
        type="button"
        className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        {open ? "Hide investigation" : label}
      </button>
      {open ? <div className="space-y-3">{children}</div> : null}
    </div>
  );
}
